import { API_URL } from '../../../../constants/urls.constants';
import type { CommonResponse } from '../../../../types/global.types';
import { appServiceApi } from '../../../apis/app-service.api';
import { APP_API_TAGS } from '../../../app.constants';
import type {
  AttendanceHealthReportParams,
  CostPerProductiveHourParams,
  CostPerProductiveHourReportParams,
  CostPerProductiveHourResponse,
  DailyIdleTimeAbuseParams,
  DailyIdleTimeAbuseReportParams,
  DailyIdleTimeAbuseResponse,
  DisciplineIssuesReportParams,
  DisciplineScoreReportParams,
  EarlyComerRatioParams,
  EarlyComerRatioReportParams,
  EarlyComerRatioResponse,
  GraceAbuseIndexParams,
  GraceAbuseIndexResponse,
  GracePeriodAbuseReportParams,
  GracePeriodAbuseStatsParams,
  GracePeriodAbuseStatsResponse,
  LateGoerRatioParams,
  LateGoerRatioReportParams,
  LateGoerRatioResponse,
  MonthlyDisciplineReportParams,
  OfficeHoursComparisonParams,
  OfficeHoursComparisonResponse,
  OfficeHoursReportParams,
  OrganizationDisciplineStatesParams,
  OrganizationDisciplineStatesResponse,
  OrgDisciplineStatsParams,
  OrgDisciplineStatsResponse,
  PayrollLeakageLostWorkingHoursParams,
  PayrollLeakageLostWorkingHoursResponse,
  PayrollLeakageReportParams,
  TodayAttendanceHealthParams,
  TodayAttendanceHealthResponse,
  TopDisciplineIssuesParams,
  TopDisciplineIssuesResponse,
  UnderperformingHighSalaryReportParams,
  UnderperformingHighSalaryUsersParams,
  UnderperformingHighSalaryUsersResponse,
} from './management-dashboard.type';

const { ATTENDANCES, EMPLOYEES, ATTENDANCE_REPORTS } = APP_API_TAGS || {};

export const managementDashboardApi = appServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    getOrgDisciplineStats: builder.query<
      CommonResponse<OrgDisciplineStatsResponse>,
      OrgDisciplineStatsParams
    >({
      query: (params) => ({
        url: `${API_URL.dashboard}/discipline-stats`,
        params,
      }),
      providesTags: () => [ATTENDANCES],
    }),

    getTodayAttendanceHealth: builder.query<
      CommonResponse<TodayAttendanceHealthResponse>,
      TodayAttendanceHealthParams | void
    >({
      query: (params) => ({
        url: `${API_URL.dashboard}/today-attendance-health`,
        params,
      }),
      providesTags: () => [ATTENDANCES],
    }),

    getTop10DisciplineIssues: builder.query<
      CommonResponse<TopDisciplineIssuesResponse>,
      TopDisciplineIssuesParams
    >({
      query: (params) => ({
        url: `${API_URL.dashboard}/top-discipline-issue-users`,
        params,
      }),
      providesTags: () => [EMPLOYEES],
    }),

    getOfficeHour: builder.query<
      CommonResponse<OfficeHoursComparisonResponse>,
      OfficeHoursComparisonParams
    >({
      query: (params) => ({
        url: `${API_URL.dashboard}/office-hours-comparison`,
        params,
      }),
      providesTags: () => [ATTENDANCES],
    }),

    getPayrollLeakageAndLostWorkingHours: builder.query<
      CommonResponse<PayrollLeakageLostWorkingHoursResponse>,
      PayrollLeakageLostWorkingHoursParams
    >({
      query: (params) => ({
        url: `${API_URL.dashboard}/payroll-leakage-lost-working-hours`,
        params,
      }),
      providesTags: () => [ATTENDANCES],
    }),

    getHighSalaryLowOutputUsers: builder.query<
      CommonResponse<UnderperformingHighSalaryUsersResponse>,
      UnderperformingHighSalaryUsersParams
    >({
      query: (params) => ({
        url: `${API_URL.dashboard}/underperforming-high-salary-users`,
        params,
      }),
      providesTags: () => [EMPLOYEES],
    }),

    getCostPerProductiveHour: builder.query<
      CommonResponse<CostPerProductiveHourResponse>,
      CostPerProductiveHourParams
    >({
      query: (params) => ({
        url: `${API_URL.dashboard}/cost-per-productive-hour`,
        params,
      }),
      providesTags: () => [ATTENDANCES],
    }),

    getGracePeriodAbuseStats: builder.query<
      CommonResponse<GracePeriodAbuseStatsResponse>,
      GracePeriodAbuseStatsParams
    >({
      query: (params) => ({
        url: `${API_URL.dashboard}/grace-period-abuse-stats`,
        params,
      }),
      providesTags: () => [ATTENDANCES],
    }),
    getEarlyComerRatio: builder.query<
      CommonResponse<EarlyComerRatioResponse>,
      EarlyComerRatioParams
    >({
      query: (params) => ({
        url: `${API_URL.dashboard}/early-comer-ratio`,
        params,
      }),
      providesTags: () => [ATTENDANCES],
    }),
    getLateGoerRatio: builder.query<
      CommonResponse<LateGoerRatioResponse>,
      LateGoerRatioParams
    >({
      query: (params) => ({
        url: `${API_URL.dashboard}/late-goer-ratio`,
        params,
      }),
      providesTags: () => [ATTENDANCES],
    }),
    getDailyIdleTimeAbuse: builder.query<
      CommonResponse<DailyIdleTimeAbuseResponse>,
      DailyIdleTimeAbuseParams
    >({
      query: (params) => ({
        url: `${API_URL.dashboard}/daily-idle-time-abuse`,
        params,
      }),
      providesTags: () => [ATTENDANCES],
    }),
    getOrganizationDisciplineStates: builder.query<
      CommonResponse<OrganizationDisciplineStatesResponse>,
      OrganizationDisciplineStatesParams
    >({
      query: (params) => ({
        url: `${API_URL.dashboard}/discipline-stats-monthly`,
        params,
      }),
      providesTags: () => [ATTENDANCES],
    }),
    getGraceAbuseIndex: builder.query<
      CommonResponse<GraceAbuseIndexResponse>,
      GraceAbuseIndexParams
    >({
      query: (params) => ({
        url: `${API_URL.dashboard}/top-grace-abusers`,
        params,
      }),
      providesTags: () => [ATTENDANCES],
    }),

    // ------ Report Generation APIs ------
    generateAttendanceHealthReport: builder.mutation<
      CommonResponse<any>,
      AttendanceHealthReportParams
    >({
      query: (body) => ({
        url: `/reports/generate-report/attendance-health`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [ATTENDANCE_REPORTS],
    }),

    generateWorkDisciplineScoreReport: builder.mutation<
      CommonResponse<any>,
      DisciplineScoreReportParams
    >({
      query: (body) => ({
        url: `/reports/generate-report/discipline`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [ATTENDANCE_REPORTS],
    }),

    generateWorkDisciplineIssuesReport: builder.mutation<
      CommonResponse<any>,
      DisciplineIssuesReportParams
    >({
      query: (body) => ({
        url: `/reports/generate-report/discipline-issues`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [ATTENDANCE_REPORTS],
    }),

    generateOfficeHoursReport: builder.mutation<
      CommonResponse<any>,
      OfficeHoursReportParams
    >({
      query: (body) => ({
        url: `/reports/generate-report/office-hours`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [ATTENDANCE_REPORTS],
    }),

    generatePayrollLeakageReport: builder.mutation<
      CommonResponse<any>,
      PayrollLeakageReportParams
    >({
      query: (body) => ({
        url: `/reports/generate-report/payroll-leakage`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [ATTENDANCE_REPORTS],
    }),

    generateHighSalaryLowOutputPerformanceReport: builder.mutation<
      CommonResponse<any>,
      UnderperformingHighSalaryReportParams
    >({
      query: (body) => ({
        url: `/reports/generate-report/underperforming-high-salary`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [ATTENDANCE_REPORTS],
    }),

    generateGracePeriodAbuseReport: builder.mutation<
      CommonResponse<any>,
      GracePeriodAbuseReportParams
    >({
      query: (body) => ({
        url: `/reports/generate-report/grace-period-abuse`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [ATTENDANCE_REPORTS],
    }),
    generateCostPerProductiveHourReport: builder.mutation<
      CommonResponse<any>,
      CostPerProductiveHourReportParams
    >({
      query: (body) => ({
        url: `/reports/generate-report/cost-per-productive-hour`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [ATTENDANCE_REPORTS],
    }),
    generateEarlyComerRatioReport: builder.mutation<
      CommonResponse<any>,
      EarlyComerRatioReportParams
    >({
      query: (body) => ({
        url: `/reports/generate-report/early-comer-ratio`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [ATTENDANCE_REPORTS],
    }),
    generateLateGoerRatioReport: builder.mutation<
      CommonResponse<any>,
      LateGoerRatioReportParams
    >({
      query: (body) => ({
        url: `/reports/generate-report/late-goer-ratio`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [ATTENDANCE_REPORTS],
    }),
    generateDailyIdleTimeAbuseReport: builder.mutation<
      CommonResponse<any>,
      DailyIdleTimeAbuseReportParams
    >({
      query: (body) => ({
        url: `/reports/generate-report/daily-idle-time-abuse`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [ATTENDANCE_REPORTS],
    }),
    generateMonthlyDisciplineReport: builder.mutation<
      CommonResponse<any>,
      MonthlyDisciplineReportParams
    >({
      query: (body) => ({
        url: `/reports/generate-report/monthly-discipline`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [ATTENDANCE_REPORTS],
    }),
  }),
});

export const {
  useGetOrgDisciplineStatsQuery,
  useGetTodayAttendanceHealthQuery,
  useGetTop10DisciplineIssuesQuery,
  useGetOfficeHourQuery,
  useGetPayrollLeakageAndLostWorkingHoursQuery,
  useGetHighSalaryLowOutputUsersQuery,
  useGetCostPerProductiveHourQuery,
  useGetGracePeriodAbuseStatsQuery,
  useGetEarlyComerRatioQuery,
  useGetLateGoerRatioQuery,
  useGetDailyIdleTimeAbuseQuery,
  useGetOrganizationDisciplineStatesQuery,
  useGetGraceAbuseIndexQuery,

  // Report Generation Hooks
  useGenerateAttendanceHealthReportMutation,
  useGenerateWorkDisciplineScoreReportMutation,
  useGenerateWorkDisciplineIssuesReportMutation,
  useGenerateOfficeHoursReportMutation,
  useGeneratePayrollLeakageReportMutation,
  useGenerateHighSalaryLowOutputPerformanceReportMutation,
  useGenerateGracePeriodAbuseReportMutation,
  useGenerateCostPerProductiveHourReportMutation,
  useGenerateEarlyComerRatioReportMutation,
  useGenerateLateGoerRatioReportMutation,
  useGenerateDailyIdleTimeAbuseReportMutation,
  useGenerateMonthlyDisciplineReportMutation,
} = managementDashboardApi;
